import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAllErrorCatalogues, useErrorCatalogues } from '../query/errors';

interface Props {
  /** The catalogue whose page is open, if any — auto-expanded. */
  activeCatalogue?: string;
  /** The code whose page is open, if any — highlighted. */
  activeCode?: string;
}

/**
 * Index of every error page, for the /errors/* section.
 *
 * Someone arrives here from one failing API call and often needs a neighbouring
 * code — the one they got last week, or the shared code the same response also
 * carried. Without this they would have to guess a URL or go back to the index.
 *
 * Branches load lazily: the full set is one request per catalogue, and a reader
 * following a `type` URI usually wants exactly one of them. The requests share
 * the cache with the page being read, so the open catalogue's branch costs
 * nothing.
 */
export default function ErrorNav({ activeCatalogue, activeCode }: Props) {
  const cataloguesQuery = useErrorCatalogues();
  const catalogues = cataloguesQuery.data ?? [];

  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(activeCatalogue ? [activeCatalogue] : []),
  );

  // Keep the open page's branch expanded, including on a deep link into a
  // catalogue the index has not loaded yet.
  useEffect(() => {
    if (activeCatalogue) {
      setExpanded((prev) =>
        prev.has(activeCatalogue) ? prev : new Set(prev).add(activeCatalogue),
      );
    }
  }, [activeCatalogue]);

  // Only expanded branches are fetched. useQueries keeps one query per
  // catalogue, so collapsing and re-expanding is free.
  const expandedIds = catalogues.map((c) => c.catalogueId).filter((id) => expanded.has(id));
  const branches = useAllErrorCatalogues(expandedIds);
  const branchFor = (catalogueId: string) => branches[expandedIds.indexOf(catalogueId)];

  const toggle = (catalogueId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(catalogueId)) {
        next.delete(catalogueId);
      } else {
        next.add(catalogueId);
      }
      return next;
    });
  };

  // The <nav> shell stretches to the full height of the layout, so its
  // background and border reach the bottom of the page; .errnav-inner is what
  // actually sticks as the reader scrolls.
  return (
    <nav aria-label="Error code index" className="errnav">
      <div className="errnav-inner">
        <Link className="errnav-title" to="/errors">
          Error catalogues
        </Link>

        {catalogues.map((catalogue) => {
          const isExpanded = expanded.has(catalogue.catalogueId);
          const branch = isExpanded ? branchFor(catalogue.catalogueId) : undefined;
          const isActive = catalogue.catalogueId === activeCatalogue;
          const codes = branch?.data?.codes ?? [];

          return (
            <div className="errnav-branch" key={catalogue.catalogueId}>
              <div className="errnav-service-row">
                <button
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${catalogue.displayName}`}
                  className="errnav-toggle"
                  onClick={() => toggle(catalogue.catalogueId)}
                >
                  <svg
                    aria-hidden={true}
                    className={`errnav-chevron${isExpanded ? ' open' : ''}`}
                    height="11"
                    viewBox="0 0 16 16"
                    width="11"
                  >
                    <path
                      d="M6 3l5 5-5 5"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
                <Link
                  className={`errnav-service${isActive ? ' active' : ''}`}
                  to={`/errors/${catalogue.catalogueId}`}
                >
                  {catalogue.displayName}
                </Link>
              </div>

              {isExpanded && (
                <div className="errnav-codes">
                  {branch?.isPending && <span className="errnav-note">Loading…</span>}
                  {branch?.isError && <span className="errnav-note">No catalogue published</span>}
                  {branch?.isSuccess && codes.length === 0 && (
                    <span className="errnav-note">No codes of its own</span>
                  )}
                  {codes.map((entry, i) => {
                    const segment = entry.path.slice(entry.path.lastIndexOf('/') + 1);
                    // The API returns current codes first, so the divider goes
                    // wherever the retired run starts.
                    const startsRetired = Boolean(entry.retiredAt) && !codes[i - 1]?.retiredAt;
                    const classes = [
                      'errnav-code',
                      isActive && segment === activeCode ? 'active' : '',
                      entry.retiredAt ? 'retired' : '',
                    ].filter(Boolean);
                    return (
                      <Fragment key={entry.path}>
                        {startsRetired && <span className="errnav-divider">Retired</span>}
                        <Link
                          className={classes.join(' ')}
                          title={
                            entry.retiredAt
                              ? `Retired ${entry.retiredAt.slice(0, 10)} — still resolves`
                              : entry.title
                          }
                          to={entry.path}
                        >
                          {segment}
                        </Link>
                      </Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
